import { useEffect, useMemo, useState } from "react";
import { useFranklinContext } from "../../context/franklin-context";
import { formatDate } from "../../lib/utils";
import type { Case, Message } from "../../types";

const STATUS_CLASSES: Record<Case["status"], string> = {
  closed: "bg-gray-100 text-gray-700",
  open: "bg-green-100 text-green-700",
  pending: "bg-yellow-100 text-yellow-700",
  resolved: "bg-gray-100 text-gray-700",
};

const AUTHOR_LABEL: Record<Message["authorType"], string> = {
  agent: "Support Agent",
  ai: "Franklin AI",
  user: "You",
};

export function Cases() {
  const { apiEndpoint, orgId, sdkKey, context } = useFranklinContext();
  const [cases, setCases] = useState<Case[]>([]);
  const [selectedCase, setSelectedCase] = useState<Case | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const resolvedUserEmail = resolveUserEmail(context);

  useEffect(() => {
    fetchCases(resolvedUserEmail).catch((error) => {
      console.error("Failed to initialise cases:", error);
    });
  }, [resolvedUserEmail]);

  const fetchCases = async (userEmail?: string) => {
    try {
      setIsLoading(true);
      const headers: Record<string, string> = {
        "X-Franklin-Org-Id": orgId,
        "X-Franklin-SDK-Key": sdkKey,
      };

      if (userEmail) {
        headers["X-Franklin-User-Email"] = userEmail;
      }

      const response = await fetch(`${apiEndpoint}/cases`, { headers });
      const data = await response.json();
      setCases(data.cases ?? []);
    } catch (error) {
      console.error("Failed to fetch cases:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCaseMessages = async (caseId: string) => {
    try {
      const response = await fetch(`${apiEndpoint}/cases/${caseId}/messages`, {
        headers: {
          "X-Franklin-Org-Id": orgId,
          "X-Franklin-SDK-Key": sdkKey,
        },
      });
      const data = await response.json();
      setMessages(data.messages ?? []);
    } catch (error) {
      console.error("Failed to fetch case messages:", error);
    }
  };

  const handleCaseClick = (caseItem: Case) => {
    setSelectedCase(caseItem);
    fetchCaseMessages(caseItem.id).catch((error) => {
      console.error("Failed to load case messages:", error);
    });
  };

  const selectedCaseStatusClass = useMemo(() => {
    if (!selectedCase) {
      return "";
    }
    return STATUS_CLASSES[selectedCase.status] ?? STATUS_CLASSES.resolved;
  }, [selectedCase]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-gray-500 text-sm">Loading cases...</div>
      </div>
    );
  }

  if (selectedCase) {
    return (
      <div className="flex h-full flex-col">
        <div className="border-[var(--franklin-border)] border-b px-6 py-4">
          <button
            className="mb-2 text-blue-600 text-sm hover:text-blue-700"
            onClick={() => setSelectedCase(null)}
            type="button"
          >
            ← Back to cases
          </button>
          <h2 className="font-semibold text-[var(--franklin-foreground)] text-lg">
            {selectedCase.subject}
          </h2>
          <div className="mt-2 flex items-center gap-2">
            <span
              className={`inline-flex items-center rounded-full px-2 py-1 font-medium text-xs ${selectedCaseStatusClass}`}
            >
              {selectedCase.status}
            </span>
            {selectedCase.priority && (
              <span className="text-gray-500 text-xs">
                Priority: {selectedCase.priority}
              </span>
            )}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-4">
            {messages.map((message) => (
              <div className="flex flex-col gap-1" key={message.id}>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-[var(--franklin-foreground)] text-xs">
                    {AUTHOR_LABEL[message.authorType]}
                  </span>
                  <span className="text-gray-500 text-xs">
                    {formatDate(message.createdAt)}
                  </span>
                </div>
                <div className="text-gray-700 text-sm">{message.body}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="border-[var(--franklin-border)] border-b px-6 py-4">
        <h2 className="font-semibold text-[var(--franklin-foreground)] text-lg">
          My Cases
        </h2>
      </div>
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {cases.length === 0 ? (
          <div className="py-8 text-center text-gray-500 text-sm">
            You don't have any open cases
          </div>
        ) : (
          <div className="space-y-3">
            {cases.map((caseItem) => (
              <button
                className="w-full rounded-lg border border-[var(--franklin-border)] p-4 text-left transition-colors hover:bg-gray-50"
                key={caseItem.id}
                onClick={() => handleCaseClick(caseItem)}
                type="button"
              >
                <div className="mb-2 flex items-start justify-between">
                  <h3 className="font-medium text-[var(--franklin-foreground)] text-sm">
                    {caseItem.subject}
                  </h3>
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-1 font-medium text-xs ${
                      STATUS_CLASSES[caseItem.status] ?? STATUS_CLASSES.resolved
                    }`}
                  >
                    {caseItem.status}
                  </span>
                </div>
                <p className="text-[var(--franklin-muted)] text-xs">
                  Updated {formatDate(caseItem.updatedAt)}
                </p>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function resolveUserEmail(
  context: ReturnType<typeof useFranklinContext>["context"]
): string | undefined {
  const metadataEmail =
    typeof context.metadata === "object" && context.metadata !== null
      ? ((context.metadata.email as string | undefined) ??
        (context.metadata.userEmail as string | undefined))
      : undefined;

  if (metadataEmail) {
    return metadataEmail;
  }

  if (typeof context.userId === "string" && context.userId.includes("@")) {
    return context.userId;
  }

  return;
}
